/**
 * Event-Editor (Dialog) — Phase 11 von #763, siehe reference/bedarfsplaner/BEDARFSPLANER.md §5
 * und reference/bedarfsplaner/Dialoge Reference.html. Bearbeitet ein Event mit Titel, Zeitraum,
 * Darstellung (Vorlage, Icon, Farbe — färbt Kopf, Balken und In-Zelle-Gruppen), Ort, Gästezahl (+ Besetzungs-Richtwert), Anlass-Typ, freien Details und den
 * Bedarfsschichten je Tag (eine .dbrow = ein Posten in einem Zeitfenster). Speichern schickt das ganze
 * { event, bedarfZeilen }-Payload (POST neu / PUT bestehend, Whole-Matrix-Abgleich serverseitig).
 *
 * Bewusste Vereinfachungen ggü. der Referenz: der Bereich einer Zeile ergibt sich aus dem gewählten
 * Posten (ein Posten-Select statt getrennter Bereich+Posten-Auswahl); Bedarfszeilen tragen keine
 * geforderte Qualifikation; die zubtn-Zuordnung ist erst nach dem ersten Speichern aktiv (vorher fehlt
 * die BedarfID). Der Übernehmen-Richtwert legt je aktivem Bereich eine Startzeile am ersten Tag an.
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { stammdatenService } from '../services/StammdatenService.js';
import { eventService } from '../services/EventService.js';
import { bedarfService } from '../services/BedarfService.js';
import { oeffneBedarfZuordnung } from './bedarfZuordnung.js';
import { farbeFuerBereich } from '../util/bereichFarben.js';
import { gaesteJeKraftFuerBereich } from '../../../../../domain/konstanten/BesetzungsRichtwerte.js';
import { zeigeKleineNachricht } from '../systemNachricht.js';
import { baueFarbAuswahl } from '../components/farbAuswahl.js';
import { bestaetige } from './bestaetigung.js';
import { convertNumberToEuroGanz } from '../util.js';
import {
	EVENT_ICONS,
	EVENT_VORLAGEN,
	EVENT_FARB_VORSCHLAEGE,
	EVENT_STANDARD_FARBE,
	eventFarbe,
	textFarbeAuf,
} from '../util/eventFarben.js';
import {
	toIso,
	baueStepper,
	bauePostenSelect,
	postenInfo,
	postenGesperrtHinweis,
	baueZeitInput,
	feld,
	textInput,
	textArea,
} from '../components/bedarfFelder.js';


let onChangeCallback = null;
let event = null;
let zeilen = [];
// Gespeicherte Zeilen mit Zuordnungen, die im Editor entfernt wurden — beim Speichern bestätigen lassen.
let entfernteMitZuordnung = [];
let postenListe = [];
let bereiche = [];
let einsatzorte = [];
let detailsVorschlaege = [];

function schliesse() {
	document.getElementById('event-editor-modal')?.remove();
}

/** Die vom Event-Zeitraum abgedeckten Kalendertage. */
function abgedeckteTage() {
	const tage = [];
	let d = dayjs(event.DatumVon).startOf('day');
	const ende = dayjs(event.DatumBis).startOf('day');
	while (d.isBefore(ende) || d.isSame(ende, 'day')) {
		tage.push(d.format('YYYY-MM-DD'));
		d = d.add(1, 'day');
	}
	return tage;
}

function baueModalHuelle() {
	schliesse();
	const modal = document.createElement('div');
	modal.className = 'modal';
	modal.id = 'event-editor-modal';
	modal.style.display = 'block';
	modal.innerHTML = `
		<div class="modal-inhalt" style="width:780px;max-height:90vh;">
			<div class="dlg" style="max-height:90vh;">
				<div class="dlg-head" id="ve-head">
					<div class="ic"><i class="bi bi-stars"></i></div>
					<div style="min-width:0;">
						<div class="ttl">${event._id ? 'Event bearbeiten' : 'Neues Event'}</div>
						<div class="sub" id="ve-sub"></div>
					</div>
					<i class="bi bi-x-lg x" id="ve-close"></i>
				</div>
				<div class="dlg-body" id="ve-body"></div>
				<div class="dlg-foot">
					<button class="dbtn danger" id="ve-delete"${event._id ? '' : ' style="display:none;"'}><i class="bi bi-trash"></i>Event löschen</button>
					<div class="sp"></div>
					<button class="dbtn" id="ve-cancel">Abbrechen</button>
					<button class="dbtn primary" id="ve-save"><i class="bi bi-check-lg"></i>Speichern</button>
				</div>
			</div>
		</div>`;
	document.body.appendChild(modal);
	modal.addEventListener('mousedown', (e) => { if (e.target === modal) schliesse(); });
	modal.querySelector('#ve-close').addEventListener('click', schliesse);
	modal.querySelector('#ve-cancel').addEventListener('click', schliesse);
	modal.querySelector('#ve-save').addEventListener('click', speichern);
	modal.querySelector('#ve-delete').addEventListener('click', loeschen);
	aktualisiereKopfFarbe();
	return modal;
}

/** Färbt den Dialogkopf in der Event-Farbe (live bei jeder Änderung der Darstellung). */
function aktualisiereKopfFarbe() {
	const kopf = document.getElementById('ve-head');
	if (!kopf) return;
	const farbe = eventFarbe(event);
	const text = textFarbeAuf(farbe);
	kopf.style.background = `linear-gradient(135deg, ${farbe}, color-mix(in srgb, ${farbe} 72%, #fff))`;
	kopf.style.color = text;
	// Die Unterzeile ist sonst hellblau (für den navy Standardkopf) und auf farbigen Event-Köpfen kaum lesbar —
	// daher immer dieselbe kontrastreiche Schriftfarbe wie der Titel (weiß bzw. dunkel auf hellen Farben).
	document.getElementById('ve-sub').style.color = text;
}

function aktualisiereSub() {
	const von = dayjs(event.DatumVon);
	const bis = dayjs(event.DatumBis);
	const tage = abgedeckteTage().length;
	const gaeste = event.Gaestezahl ? ` <span style="opacity:.5;">·</span> <i class="bi bi-people-fill"></i>${event.Gaestezahl} Gäste` : '';
	// Geplante AG-Kosten des Events (Summe der Bedarfszeilen, wie die Kostenanzeige im Bedarfsplaner) — nur wenn
	// der Server Kosten geliefert hat, also für Rollen mit Budget-Einsicht und bei bestehenden Events.
	const mitKosten = zeilen.some((z) => z.geplant !== undefined);
	const kosten = mitKosten
		? ` <span style="opacity:.5;">·</span> <span title="Geplante AG-Kosten: nur zugeordnete Schichten · offene Posten ohne Kosten" style="display:inline-flex;align-items:center;gap:8px;"><i class="bi bi-cash-coin"></i>${convertNumberToEuroGanz(zeilen.reduce((summe, z) => summe + (z.geplant || 0), 0))} geplant</span>`
		: '';
	document.getElementById('ve-sub').innerHTML = `<i class="bi bi-calendar3"></i>${von.format('dd, DD.MM.')} – ${bis.format('dd, DD.MM.YYYY')}${gaeste} <span style="opacity:.5;">·</span> <i class="bi bi-clipboard2-check"></i>Bedarf für ${tage} ${tage === 1 ? 'Tag' : 'Tage'}${kosten}`;
}

/** Badge "N Tage" neben dem Zeitraum (live bei Datumsänderung). */
function aktualisiereTageBadge() {
	const badge = document.getElementById('ve-tage');
	if (!badge) return;
	const tageN = abgedeckteTage().length;
	badge.textContent = `${tageN} ${tageN === 1 ? 'Tag' : 'Tage'}`;
}

/** Zuordnungs-Button einer Zeile: "zugeordnet/benötigt" samt Zustand (live bei -/+ im Stepper). */
function aktualisiereZubtn(zubtn, filled, anzahl) {
	const zustand = filled >= anzahl ? 'full' : (filled === 0 ? 'empty' : 'part');
	zubtn.className = `zubtn ${zustand}`;
	const icon = zustand === 'full' ? 'bi-check-circle-fill' : 'bi-person-plus';
	zubtn.innerHTML = `<i class="bi ${icon}"></i>${filled}/${anzahl}`;
}

function schmueckeAreatag(areatag, zeile) {
	const farbe = zeile.BereichID ? farbeFuerBereich(bereiche, zeile.BereichID) : 'var(--muted)';
	areatag.style.color = farbe;
	areatag.style.background = `${farbe}1a`; // ~10% alpha
	areatag.querySelector('.at-name').textContent = zeile.Bereich || 'Bereich';
}

/** Eine Bedarfsschicht-Zeile (.dbrow). */
function baueDbRow(zeile) {
	const row = document.createElement('div');
	row.className = 'dbrow';
	const filled = zeile.filled || 0;

	const areatag = document.createElement('span');
	areatag.className = 'areatag';
	areatag.innerHTML = '<span class="at-name"></span>';
	schmueckeAreatag(areatag, zeile);
	row.appendChild(areatag);

	// Posten ist gesperrt, solange Mitarbeitende zugeordnet sind (serverseitig ebenso geprüft).
	row.appendChild(bauePostenSelect({
		bereiche,
		postenListe,
		postenID: zeile.PostenID,
		gesperrt: filled > 0,
		onChange: (info) => {
			zeile.PostenID = info.postenID;
			zeile.Posten = info.posten;
			zeile.BereichID = info.bereichID;
			zeile.Bereich = info.bereich;
			schmueckeAreatag(areatag, zeile);
		},
	}));
	row.appendChild(baueZeitInput(zeile.von, (v) => { zeile.von = v; }));
	const arrow = document.createElement('div');
	arrow.className = 'arrow';
	arrow.innerHTML = '<i class="bi bi-arrow-right"></i>';
	row.appendChild(arrow);
	row.appendChild(baueZeitInput(zeile.bis, (v) => { zeile.bis = v; }));
	// Zuordnungs-Button: nur für bereits gespeicherte Zeilen (mit _id) aktiv.
	const zubtn = document.createElement('span');
	aktualisiereZubtn(zubtn, filled, zeile.anzahl);
	row.appendChild(baueStepper({ wert: zeile.anzahl, min: Math.max(1, filled), onChange: (n) => { zeile.anzahl = n; aktualisiereZubtn(zubtn, filled, n); } }));
	if (zeile._id) {
		zubtn.addEventListener('click', () => oeffneBedarfZuordnung(zeile._id, () => ladeUndRendere(event._id)));
	} else {
		zubtn.style.opacity = '.6';
		zubtn.addEventListener('click', () => zeigeKleineNachricht('Bitte zuerst speichern, dann zuweisen.', 0));
	}
	row.appendChild(zubtn);

	const del = document.createElement('span');
	del.className = 'del';
	del.innerHTML = '<i class="bi bi-trash"></i>';
	del.addEventListener('click', () => {
		zeilen = zeilen.filter((z) => z !== zeile);
		if (zeile._id && filled > 0) entfernteMitZuordnung.push(zeile);
		rendereBody();
	});
	row.appendChild(del);

	if (filled > 0) {
		const hinweis = postenGesperrtHinweis(filled);
		hinweis.style.gridColumn = '1 / -1';
		row.appendChild(hinweis);
	}
	return row;
}

function neueZeile(tag) {
	const erstePosten = postenListe[0];
	const info = postenInfo(bereiche, postenListe, erstePosten?._id);
	return {
		PostenID: erstePosten?._id || '',
		Posten: erstePosten?.Posten || '',
		BereichID: info.bereichID,
		Bereich: info.bereich,
		tag,
		von: '17:00',
		bis: '23:00',
		anzahl: 1,
		filled: 0,
	};
}

/** Baut den kompletten Dialog-Body (Felder + Tagesbedarf) neu auf. */
function rendereBody() {
	aktualisiereSub();
	const body = document.getElementById('ve-body');
	body.innerHTML = '';

	// Vorlage ganz oben (belegt Titel/Anlass-Typ vor), Icon/Farbe weiter unten nach dem Zeitraum
	const [vorlageFeld, iconFarbeReihe] = baueDarstellung();
	body.appendChild(vorlageFeld);

	// Titel
	const titelInput = textInput(event.Titel, (v) => { event.Titel = v; });
	titelInput.id = 've-titel';
	body.appendChild(feld('Titel', titelInput));

	// Zeitraum
	const zeitraum = document.createElement('div');
	zeitraum.className = 'daterange';
	const vonInput = dateTimeInput(event.DatumVon, (v) => { event.DatumVon = v; aktualisiereSub(); aktualisiereTageBadge(); rendereTagesbedarf(); });
	const bisInput = dateTimeInput(event.DatumBis, (v) => { event.DatumBis = v; aktualisiereSub(); aktualisiereTageBadge(); rendereTagesbedarf(); });
	const pfeil = document.createElement('i');
	pfeil.className = 'bi bi-arrow-right';
	pfeil.style.color = 'var(--muted-2)';
	const spanBadge = document.createElement('span');
	spanBadge.className = 'span-badge';
	spanBadge.id = 've-tage';
	zeitraum.append(vonInput, pfeil, bisInput, spanBadge);
	body.appendChild(feld('Zeitraum', zeitraum));
	aktualisiereTageBadge();

	// Icon / Farbe
	body.appendChild(iconFarbeReihe);

	// Einsatzort
	body.appendChild(feld('Einsatzort', baueOrtSelect()));

	// Gäste / Anlass-Typ (wie in der Referenz nebeneinander — die Gästezahl braucht keine volle Breite)
	const row2 = document.createElement('div');
	row2.className = 'row2';
	row2.appendChild(baueGaesteFeld());
	const anlassInput = textInput(event.AnlassTyp, (v) => { event.AnlassTyp = v; }, 'z. B. Hochzeit');
	anlassInput.id = 've-anlass';
	row2.appendChild(feld('Anlass-Typ', anlassInput));
	body.appendChild(row2);

	// Details
	body.appendChild(baueDetails());

	// Bedarfsschichten je Tag
	const bedarfFeld = feld('Bedarfsschichten je Tag', document.createElement('div'));
	bedarfFeld.lastChild.className = 'daybedarf';
	bedarfFeld.lastChild.id = 've-daybedarf';
	body.appendChild(bedarfFeld);
	rendereTagesbedarf();

	// Notiz
	body.appendChild(feld('Notiz', textArea(event.Notiz, (v) => { event.Notiz = v; })));
}

function rendereTagesbedarf() {
	const container = document.getElementById('ve-daybedarf');
	if (!container) return;
	container.innerHTML = '';
	// Zeilen, deren Tag nicht mehr abgedeckt ist, auf den ersten Tag ziehen.
	const tage = abgedeckteTage();
	zeilen.forEach((z) => { if (!tage.includes(z.tag)) z.tag = tage[0]; });

	tage.forEach((tag) => {
		const dbday = document.createElement('div');
		dbday.className = 'dbday';
		const dbh = document.createElement('div');
		dbh.className = 'dbh';
		dbh.innerHTML = `<i class="bi bi-calendar3" style="color:var(--muted-2);"></i>${dayjs(tag).format('dddd, DD.MM.')}<span class="sp"></span>`;
		const addrow = document.createElement('span');
		addrow.className = 'addrow';
		addrow.innerHTML = '<i class="bi bi-plus-lg"></i>Schicht';
		addrow.addEventListener('click', () => { zeilen.push(neueZeile(tag)); rendereTagesbedarf(); });
		dbh.appendChild(addrow);
		dbday.appendChild(dbh);
		zeilen.filter((z) => z.tag === tag).forEach((z) => dbday.appendChild(baueDbRow(z)));
		container.appendChild(dbday);
	});
}

// --- kleine Feld-Bausteine ---

function dateTimeInput(isoWert, onChange) {
	const wrap = document.createElement('div');
	wrap.className = 'ctl';
	wrap.style.flex = '1';
	const inp = document.createElement('input');
	inp.className = 'inp';
	inp.type = 'datetime-local';
	inp.style.width = '100%';
	inp.value = isoWert ? dayjs(isoWert).format('YYYY-MM-DDTHH:mm') : '';
	inp.addEventListener('change', () => onChange(inp.value ? dayjs(inp.value).toISOString() : ''));
	wrap.appendChild(inp);
	return wrap;
}

/**
 * Setzt ein Textfeld des Events (und sein Input im Dialog), falls es noch leer ist.
 * @param {string} feldName Eigenschaft von `event`, z.B. 'Titel'
 * @param {string} inputID ID des Inputs im Dialog
 * @param {string} wert
 */
function fuelleWennLeer(feldName, inputID, wert) {
	if ((event[feldName] || '').trim()) return;
	event[feldName] = wert;
	const input = document.getElementById(inputID);
	if (input) input.value = wert;
}

/**
 * Darstellung des Events: Vorlagen (Icon + Farbe in einem Klick), Icon-Swatches in der Event-Farbe und die
 * Farbauswahl (Farbwähler + Vorschläge). Jede Änderung färbt den Dialogkopf live mit.
 * @returns {array<HTMLElement>} Vorlagen-Feld und die Icon/Farbe-Zeile
 */
function baueDarstellung() {
	const vorlagen = document.createElement('div');
	vorlagen.className = 'evvorlagen';
	const icons = document.createElement('div');
	icons.className = 'swatches';
	icons.style.cssText = 'height:40px;align-items:center;';

	const aktualisiere = () => {
		const farbe = eventFarbe(event);
		const text = textFarbeAuf(farbe);
		icons.querySelectorAll('.sw').forEach((sw) => {
			sw.style.background = farbe;
			sw.style.color = text;
			sw.classList.toggle('on', sw.dataset.icon === event.Icon);
		});
		vorlagen.querySelectorAll('.evvorlage').forEach((chip) => {
			chip.classList.toggle('on', chip.dataset.icon === event.Icon && chip.dataset.farbe.toLowerCase() === farbe.toLowerCase());
		});
		aktualisiereKopfFarbe();
	};

	const farbAuswahl = baueFarbAuswahl({
		wert: eventFarbe(event),
		vorschlaege: EVENT_FARB_VORSCHLAEGE,
		onChange: (farbe) => { event.Farbe = farbe; aktualisiere(); },
	});

	EVENT_VORLAGEN.forEach((vorlage) => {
		const chip = document.createElement('button');
		chip.type = 'button';
		chip.className = 'evvorlage';
		chip.dataset.icon = vorlage.icon;
		chip.dataset.farbe = vorlage.farbe;
		chip.style.setProperty('--evf', vorlage.farbe);
		chip.innerHTML = `<i class="bi bi-${vorlage.icon}"></i>${vorlage.name}`;
		chip.addEventListener('click', () => {
			event.Icon = vorlage.icon;
			event.Farbe = vorlage.farbe;
			farbAuswahl.setzeWert(vorlage.farbe);
			// Titel und Anlass-Typ mit dem Vorlagennamen vorbelegen — nur solange sie noch leer sind.
			fuelleWennLeer('Titel', 've-titel', vorlage.name);
			fuelleWennLeer('AnlassTyp', 've-anlass', vorlage.name);
			aktualisiere();
		});
		vorlagen.appendChild(chip);
	});

	EVENT_ICONS.forEach((ic) => {
		const sw = document.createElement('span');
		sw.className = 'sw';
		sw.dataset.icon = ic;
		sw.style.cssText = 'display:flex;align-items:center;justify-content:center;font-size:13px;';
		sw.innerHTML = `<i class="bi bi-${ic}"></i>`;
		sw.addEventListener('click', () => { event.Icon = ic; aktualisiere(); });
		icons.appendChild(sw);
	});
	aktualisiere();

	const reihe = document.createElement('div');
	reihe.className = 'row2';
	reihe.append(feld('Icon', icons), feld('Farbe', farbAuswahl.element));
	return [feld('Vorlage', vorlagen), reihe];
}

function baueOrtSelect() {
	const sel = document.createElement('select');
	sel.className = 'inp';
	const leer = document.createElement('option');
	leer.value = '';
	leer.textContent = '– kein Einsatzort –';
	sel.appendChild(leer);
	einsatzorte.forEach((e) => {
		const opt = document.createElement('option');
		opt.value = e._id;
		opt.textContent = e.Bezeichnung;
		if (e._id === event.EinsatzortID) opt.selected = true;
		sel.appendChild(opt);
	});
	sel.addEventListener('change', () => { event.EinsatzortID = sel.value; });
	return sel;
}

function baueGaesteFeld() {
	const f = document.createElement('div');
	f.className = 'fld';
	f.innerHTML = '<label>Erwartete Gäste <span class="opt">optional</span></label>';
	const gcount = document.createElement('div');
	gcount.className = 'gcount';
	const inp = document.createElement('input');
	inp.type = 'number';
	inp.min = '0';
	inp.className = 'gv';
	inp.placeholder = 'z. B. 120';
	inp.value = event.Gaestezahl ?? '';
	// Klick irgendwo ins Feld (Icon, "Gäste") setzt den Cursor in die Zahl.
	gcount.addEventListener('click', () => inp.focus());
	inp.addEventListener('input', () => { event.Gaestezahl = inp.value === '' ? null : parseInt(inp.value, 10); aktualisiereSub(); aktualisiereGhint(); });
	gcount.innerHTML = '<i class="bi bi-people-fill"></i>';
	gcount.appendChild(inp);
	const gu = document.createElement('span');
	gu.className = 'gu';
	gu.textContent = 'Gäste';
	gcount.appendChild(gu);
	f.appendChild(gcount);
	const ghint = document.createElement('div');
	ghint.className = 'ghint';
	ghint.id = 've-ghint';
	f.appendChild(ghint);
	setTimeout(aktualisiereGhint, 0);
	return f;
}

function aktualisiereGhint() {
	const ghint = document.getElementById('ve-ghint');
	if (!ghint) return;
	if (!event.Gaestezahl) {
		ghint.style.display = 'none';
		return;
	}
	ghint.style.display = 'flex';
	const aktiveBereiche = bereiche.filter((b) => postenListe.some((p) => p.BereichID === b._id));
	const vorschlaege = aktiveBereiche.map((b) => ({ bereich: b, anzahl: Math.max(1, Math.ceil(event.Gaestezahl / gaesteJeKraftFuerBereich(b.Bereich))) }));
	const text = vorschlaege.map((v) => `${v.anzahl} ${v.bereich.Bereich}`).join(', ');
	ghint.innerHTML = `<i class="bi bi-magic"></i>Richtwert → <b>Vorschlag ${text}</b>`;
	const apply = document.createElement('span');
	apply.className = 'gapply';
	apply.textContent = 'Übernehmen';
	apply.addEventListener('click', () => uebernehmeVorschlag(vorschlaege));
	ghint.appendChild(apply);
}

function uebernehmeVorschlag(vorschlaege) {
	const tag = abgedeckteTage()[0];
	// Je aktivem Bereich eine Startzeile am ersten Tag anlegen (bestehende Zeilen bleiben unangetastet).
	vorschlaege.forEach((v) => {
		const erstePosten = postenListe.find((p) => p.BereichID === v.bereich._id);
		if (!erstePosten) return;
		zeilen.push({
			PostenID: erstePosten._id,
			Posten: erstePosten.Posten,
			BereichID: v.bereich._id,
			Bereich: v.bereich.Bereich,
			tag,
			von: '17:00',
			bis: '23:00',
			anzahl: v.anzahl,
			filled: 0,
		});
	});
	rendereTagesbedarf();
}

function baueDetails() {
	const f = document.createElement('div');
	f.className = 'fld';
	f.innerHTML = '<label>Details <span class="opt">frei definierbar</span></label>';
	const list = document.createElement('div');
	list.className = 'kvlist';
	const zeichneListe = () => {
		list.innerHTML = '';
		event.Details.forEach((feldObj) => {
			const row = document.createElement('div');
			row.className = 'kvrow';
			const k = document.createElement('input');
			k.className = 'kvk';
			k.value = feldObj.Schluessel;
			k.placeholder = 'Feldname …';
			k.addEventListener('input', () => { feldObj.Schluessel = k.value; });
			const v = document.createElement('input');
			v.className = 'kvv';
			v.value = feldObj.Wert;
			v.placeholder = 'Wert …';
			v.addEventListener('input', () => { feldObj.Wert = v.value; });
			const del = document.createElement('span');
			del.className = 'del';
			del.innerHTML = '<i class="bi bi-trash"></i>';
			del.addEventListener('click', () => { event.Details = event.Details.filter((x) => x !== feldObj); zeichneListe(); });
			row.append(k, v, del);
			list.appendChild(row);
		});
	};
	zeichneListe();
	f.appendChild(list);

	const foot = document.createElement('div');
	foot.className = 'kvfoot';
	const addrow = document.createElement('span');
	addrow.className = 'addrow';
	addrow.innerHTML = '<i class="bi bi-plus-lg"></i>Feld hinzufügen';
	addrow.addEventListener('click', () => { event.Details.push({ Schluessel: '', Wert: '' }); zeichneListe(); });
	foot.appendChild(addrow);
	const sp = document.createElement('span');
	sp.className = 'sp';
	foot.appendChild(sp);
	if (detailsVorschlaege.length > 0) {
		const sug = document.createElement('span');
		sug.className = 'kvsug';
		sug.textContent = 'Vorschläge:';
		foot.appendChild(sug);
		detailsVorschlaege.forEach((name) => {
			const chip = document.createElement('span');
			chip.className = 'kvchip';
			chip.innerHTML = `<i class="bi bi-plus"></i>${name}`;
			chip.addEventListener('click', () => { event.Details.push({ Schluessel: name, Wert: '' }); zeichneListe(); });
			foot.appendChild(chip);
		});
	}
	f.appendChild(foot);
	return f;
}

/** Baut die Bedarfszeilen-Payloads aus den Editor-Zeilen. */
function baueBedarfZeilen() {
	const bs = stammdatenService.aktuelleBetriebsstaette;
	// Titel und Einsatzort des Events an jede Bedarfszeile durchreichen, damit die daraus
	// erzeugten Schichten sie erben (siehe SchichtService.erstelleSchichtAusBedarf).
	const einsatzortName = event.EinsatzortID ? (einsatzorte.find((e) => e._id === event.EinsatzortID)?.Bezeichnung || '') : '';
	return zeilen.filter((z) => z.PostenID).map((z) => {
		const zeile = {
			BetriebsstaetteID: bs,
			BereichID: z.BereichID,
			Bereich: z.Bereich,
			PostenID: z.PostenID,
			Posten: z.Posten,
			EinsatzortID: event.EinsatzortID || '',
			Einsatzort: einsatzortName,
			Titel: event.Titel || '',
			Datum: `${z.tag}T00:00:00`,
			SchichtVon: toIso(z.tag, z.von),
			SchichtBis: toIso(z.tag, z.bis, true, z.von),
			BedarfAnzahl: z.anzahl,
		};
		if (z._id) zeile._id = z._id;
		return zeile;
	});
}

async function speichern() {
	if (!event.Titel || !event.Titel.trim()) {
		zeigeKleineNachricht('Bitte einen Titel angeben.', 0);
		return;
	}
	// Entfernte Zeilen mit Zuordnungen: deren Schichten werden mitgelöscht — nur mit ausdrücklicher Bestätigung.
	let schichtenLoeschen = false;
	if (entfernteMitZuordnung.length > 0) {
		const anzahl = entfernteMitZuordnung.reduce((summe, z) => summe + z.filled, 0);
		const posten = entfernteMitZuordnung.map((z) => `${z.Posten} (${dayjs(z.tag).format('DD.MM.')} ${z.von}–${z.bis})`).join(', ');
		const ok = await bestaetige({
			titel: 'Zugeordnete Schichten ebenfalls löschen?',
			text: `Entfernte Bedarfszeilen haben noch Zuordnungen: ${posten}.\n${anzahl} zugeordnete Schicht(en) werden ebenfalls gelöscht.`,
			okText: 'Speichern und löschen',
		});
		if (!ok) return;
		schichtenLoeschen = true;
	}
	const payload = {
		...event,
		BetriebsstaetteID: stammdatenService.aktuelleBetriebsstaette,
	};
	const gespeichert = await eventService.speichereEvent(payload, baueBedarfZeilen(), schichtenLoeschen);
	if (!gespeichert) return;
	entfernteMitZuordnung = [];
	meldeSchichtenBericht(gespeichert.SchichtenBericht);
	if (typeof onChangeCallback === 'function') onChangeCallback();
	// Nach dem Speichern neu laden, damit neue Zeilen ihre _id/filled bekommen (zubtn aktiv).
	await ladeUndRendere(gespeichert._id);
}

/** Warnt, wenn Änderungen nicht an alle zugeordneten Schichten weitergereicht werden konnten. */
function meldeSchichtenBericht(bericht) {
	if (!bericht) return;
	const nichtAngepasst = bericht.abweichend.length + bericht.fehler.length;
	if (nichtAngepasst > 0) {
		zeigeKleineNachricht(`${nichtAngepasst} zugeordnete Schicht(en) wurden nicht angepasst (von Hand geändert oder Konflikt). Bitte im Bedarf-Dialog prüfen.`, 0);
	}
}

async function loeschen() {
	if (!event._id) { schliesse(); return; }
	const loeschenOk = await bestaetige({
		titel: 'Event löschen?',
		text: 'Die zugehörigen Bedarfszeilen werden ebenfalls entfernt.',
		okText: 'Löschen',
	});
	if (!loeschenOk) return;
	// Hat eine Zeile Zuordnungen, zusätzlich bestätigen lassen, dass deren Schichten mitgelöscht werden.
	const alleZeilen = [...zeilen, ...entfernteMitZuordnung];
	const anzahl = alleZeilen.reduce((summe, z) => summe + (z.filled || 0), 0);
	if (anzahl > 0) {
		const schichtenOk = await bestaetige({
			titel: 'Zugeordnete Schichten ebenfalls löschen?',
			text: `Dem Event sind noch ${anzahl} Mitarbeitende zugeordnet. Deren Schichten werden ebenfalls gelöscht.`,
			okText: 'Alles löschen',
		});
		if (!schichtenOk) return;
	}
	const ok = await eventService.entferneEvent(event._id, anzahl > 0);
	if (!ok) return;
	if (typeof onChangeCallback === 'function') onChangeCallback();
	schliesse();
}

/** Lädt Stammdaten + (bei bestehendem Event) dessen Bedarfszeilen und rendert den Editor. */
async function ladeUndRendere(eventID) {
	const unternehmen = await stammdatenService.getUnternehmensdaten();
	bereiche = (unternehmen?.Bereiche || []).filter((b) => b.Verwenden);
	postenListe = await stammdatenService.getPosten();
	einsatzorte = await stammdatenService.getEinsatzorte();
	detailsVorschlaege = await eventService.holeDetailsVorschlaege();

	if (eventID) {
		// Bestehendes Event: das Event + ihre Bedarfszeilen (mit filled) laden.
		const alle = await eventService.holeEventsZeitraum(dayjs(event.DatumVon).subtract(1, 'day'), dayjs(event.DatumBis).add(1, 'day'));
		const gefunden = alle.find((v) => v._id === eventID);
		if (gefunden) {
			event = { ...gefunden };
			event.Details = event.Details || [];
		}
		const bedarfe = await bedarfService.holeBedarfZeitraum(dayjs(event.DatumVon), dayjs(event.DatumBis));
		zeilen = bedarfe.filter((b) => b.EventID === eventID).map((b) => ({
			_id: b._id,
			PostenID: b.PostenID,
			Posten: b.Posten,
			BereichID: b.BereichID,
			Bereich: b.Bereich,
			tag: dayjs(b.Datum).format('YYYY-MM-DD'),
			von: dayjs(b.SchichtVon).format('HH:mm'),
			bis: dayjs(b.SchichtBis).format('HH:mm'),
			anzahl: b.BedarfAnzahl,
			filled: b.filled || 0,
			// geplante AG-Kosten der zugeordneten Schichten — kommt nur für Rollen mit Budget-Einsicht vom Server
			geplant: b.geplant,
		}));
	}
	// Altdaten ohne (gültige) Farbe/Icon bekommen die Standard-Darstellung.
	event.Farbe = eventFarbe(event);
	event.Icon = event.Icon || 'stars';
	baueModalHuelle();
	rendereBody();
}

/**
 * Öffnet den Event-Editor.
 * @param {object|null} vorhandenesEvent bestehendes Event (mit _id) oder null für ein neues Event
 * @param {dayjs} monatAnker sichtbarer Monat (für die Vorbelegung bei einer neuen Event)
 * @param {function} onChange Callback zum Neuladen des Monatsgrids
 * @param {dayjs} [startTag] Tag, an dem ein neues Event beginnt (statt am Monatsanfang)
 */
async function oeffneEventEditor(vorhandenesEvent, monatAnker, onChange, startTag = undefined) {
	onChangeCallback = onChange;
	entfernteMitZuordnung = [];
	if (vorhandenesEvent && vorhandenesEvent._id) {
		event = { ...vorhandenesEvent, Details: vorhandenesEvent.Details || [] };
		await ladeUndRendere(vorhandenesEvent._id);
	} else {
		// Neues Event: am übergebenen Tag (Tageszelle im Bedarfsplaner), sonst am Monatsanfang — jeweils 17–23 Uhr
		const start = (startTag ? dayjs(startTag).startOf('day') : (monatAnker || dayjs()).startOf('month')).hour(17);
		event = {
			Titel: '',
			Icon: 'stars',
			EinsatzortID: '',
			DatumVon: start.toISOString(),
			DatumBis: start.hour(23).toISOString(),
			Gaestezahl: null,
			AnlassTyp: '',
			Details: [],
			Ansprechpartner: '',
			Notiz: '',
			Farbe: EVENT_STANDARD_FARBE,
		};
		zeilen = [];
		await ladeUndRendere(null);
	}
}

export { oeffneEventEditor };
